import { SYNC_OP_LABEL, SyncStatusCell } from "@/components/admin/SyncStatusCell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { superadminService } from "@/lib/api";
import type {
  ChannexSyncRow,
  LlmUsageRow,
  QueuedProperty,
  ServiceSummary,
} from "@/lib/api/superadmin";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck,
  BedDouble,
  BookOpen,
  Building2,
  CalendarCheck,
  Coins,
  type LucideIcon,
  RadioTower,
  MessageSquareText,
  UserCheck,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";

interface Column {
  key: string;
  label: string;
}

/** Curated columns per collection — key fields only, noisy caches hidden. */
const COLLECTIONS: { name: string; label: string; columns: Column[] }[] = [
  {
    name: "properties",
    label: "숙소",
    columns: [
      { key: "name", label: "숙소명" },
      { key: "channex_status", label: "CMS 상태" },
      { key: "support_email", label: "지원 연락" },
      { key: "address", label: "주소" },
      { key: "phone", label: "전화" },
      { key: "check_in_time", label: "체크인" },
      { key: "check_out_time", label: "체크아웃" },
      { key: "contract_required", label: "계약 필수" },
      { key: "updated_at", label: "수정일" },
    ],
  },
  {
    name: "admin_users",
    label: "관리자",
    columns: [
      { key: "email", label: "이메일" },
      { key: "name", label: "이름" },
      { key: "is_active", label: "활성" },
      { key: "created_at", label: "가입일" },
    ],
  },
  {
    name: "room_types",
    label: "객실 타입",
    columns: [
      { key: "name", label: "이름" },
      { key: "base_rate", label: "기본요금" },
      { key: "capacity", label: "정원" },
      { key: "is_active", label: "활성" },
      { key: "updated_at", label: "수정일" },
    ],
  },
  {
    name: "rooms",
    label: "호실",
    columns: [
      { key: "name", label: "이름" },
      { key: "floor", label: "층" },
      { key: "max_guests", label: "최대인원" },
      { key: "memo", label: "메모" },
      { key: "is_active", label: "활성" },
      { key: "updated_at", label: "수정일" },
    ],
  },
  {
    name: "reservations",
    label: "예약",
    columns: [
      { key: "guest_name", label: "게스트" },
      { key: "check_in", label: "체크인" },
      { key: "check_out", label: "체크아웃" },
      { key: "status", label: "상태" },
      { key: "channel", label: "채널" },
      { key: "nightly_rate", label: "1박 요금" },
      { key: "total_amount", label: "총액" },
      { key: "created_at", label: "생성일" },
    ],
  },
  {
    name: "guests",
    label: "게스트",
    columns: [
      { key: "name", label: "이름" },
      { key: "email", label: "이메일" },
      { key: "phone", label: "전화" },
      { key: "language", label: "언어" },
      { key: "check_in", label: "체크인" },
      { key: "check_out", label: "체크아웃" },
      { key: "is_active", label: "활성" },
    ],
  },
  {
    name: "chat_messages",
    label: "채팅",
    columns: [
      { key: "role", label: "역할" },
      { key: "content", label: "내용" },
      { key: "created_at", label: "시각" },
    ],
  },
  {
    name: "guide_contents",
    label: "가이드",
    columns: [
      { key: "category", label: "분류" },
      { key: "title", label: "제목" },
      { key: "is_published", label: "공개" },
      { key: "show_as_button", label: "버튼 노출" },
      { key: "updated_at", label: "수정일" },
    ],
  },
  {
    name: "contracts",
    label: "계약서",
    columns: [
      { key: "language", label: "언어" },
      { key: "body", label: "본문" },
      { key: "updated_at", label: "수정일" },
    ],
  },
  {
    name: "channex_mappings",
    label: "Channex",
    columns: [
      { key: "kind", label: "종류" },
      { key: "local_id", label: "로컬 ID" },
      { key: "channex_id", label: "Channex ID" },
      { key: "updated_at", label: "수정일" },
    ],
  },
];

const QUEUE_BADGE: Record<string, { label: string; className: string }> = {
  pending_approval: { label: "승인 대기", className: "bg-yellow-100 text-yellow-800" },
  pending: { label: "연결 대기", className: "bg-amber-100 text-amber-700" },
  approved: { label: "승인 완료", className: "bg-sky-100 text-sky-700" },
  integrating: { label: "연동 진행중", className: "bg-indigo-100 text-indigo-700" },
};

const OTA_LABEL: Record<string, string> = {
  agoda: "Agoda",
  booking: "Booking.com",
  tripcom: "Trip.com",
};

const PAGE_SIZE = 50;

/** LLM 호출 용도 — api `models/llm_usage.PURPOSES` 와 같은 키를 쓴다. */
const PURPOSE_LABEL: Record<string, string> = {
  guest_chat: "게스트 챗",
  property_chat: "숙소 챗",
  translate: "번역",
  intent: "의도분류",
};

const number = new Intl.NumberFormat("ko-KR");

/** 추정 비용은 소액이라 소수 넷째 자리까지 — 반올림해 0으로 보이면 못 읽는다. */
function money(value: number, currency: string): string {
  return `${value.toFixed(4)} ${currency}`;
}

function totalCost(rows: LlmUsageRow[]): number {
  return rows.reduce((sum, r) => sum + r.cost, 0);
}

/** 용도별 호출 수를 "게스트 챗 12 · 번역 3" 한 줄로. */
function breakdown(counts: Record<string, number>, labels?: Record<string, string>): string {
  const parts = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([key, n]) => `${labels?.[key] ?? key} ${number.format(n)}`);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** 한 방향이라도 실패로 남아 있으면 그 숙소는 실패 건이다. */
function hasSyncFailure(row: ChannexSyncRow): boolean {
  return Object.values(row.sync).some((state) => state?.status === "failed");
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "✓" : "—";
  if (typeof value === "number") return value.toLocaleString("ko-KR");
  if (typeof value === "string" && ISO_DATETIME.test(value))
    return value.slice(0, 16).replace("T", " ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const compact = new Intl.NumberFormat("ko-KR", { notation: "compact" });

/** KPI stat tile — label + compact value (no time series yet, so no delta/trend). */
function StatTile({
  icon: Icon,
  label,
  value,
}: { icon: LucideIcon; label: string; value: number }) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="eyebrow-brass truncate leading-none">{label}</p>
        <p className="mt-1.5 text-2xl font-semibold leading-none text-foreground">
          {compact.format(value)}
        </p>
      </div>
    </div>
  );
}

/** Horizontal bar comparison of accounts by reservations; doubles as the selector. */
function ServiceBars({
  services,
  selectedId,
  onSelect,
}: {
  services: ServiceSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const ranked = [...services].sort((a, b) => b.counts.reservations - a.counts.reservations);
  const max = Math.max(1, ...ranked.map((s) => s.counts.reservations));
  return (
    <div className="space-y-1">
      {ranked.map((svc) => {
        const dimmed = selectedId !== null && selectedId !== svc.id;
        return (
          <button
            key={svc.id}
            type="button"
            onClick={() => onSelect(svc.id)}
            title={`${svc.name} · 예약 ${svc.counts.reservations.toLocaleString("ko-KR")}건`}
            className="group flex h-11 w-full cursor-pointer items-center gap-3 rounded-md px-2 transition-colors hover:bg-secondary/60"
          >
            <span
              className={cn(
                "w-32 shrink-0 truncate text-left text-sm sm:w-40",
                dimmed ? "text-muted-foreground" : "font-medium text-foreground"
              )}
            >
              {svc.name}
            </span>
            <span className="relative h-5 flex-1">
              <span
                className={cn(
                  "absolute inset-y-0 left-0 rounded-r-[4px] transition-all duration-200 motion-reduce:transition-none",
                  dimmed ? "bg-primary/20" : "bg-primary"
                )}
                style={{ width: `${(svc.counts.reservations / max) * 100}%` }}
              />
            </span>
            <span className="w-14 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
              {svc.counts.reservations.toLocaleString("ko-KR")}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Read-only cross-tenant ops console. Superadmins only (others are bounced). */
export function SuperAdminPage() {
  const user = useAuthStore((s) => s.user);
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [collection, setCollection] = useState(COLLECTIONS[0]);
  const [page, setPage] = useState(0);

  const { data: services } = useQuery({
    queryKey: ["superadmin-services"],
    queryFn: superadminService.listServices,
    enabled: Boolean(user?.is_superadmin),
  });

  const queryClient = useQueryClient();

  const { data: queue } = useQuery({
    queryKey: ["superadmin-property-queue"],
    queryFn: superadminService.listPropertyQueue,
    enabled: Boolean(user?.is_superadmin),
  });

  const { data: syncHealth } = useQuery({
    queryKey: ["superadmin-channex-sync"],
    queryFn: superadminService.channexSyncHealth,
    enabled: Boolean(user?.is_superadmin),
  });

  const { data: llmUsage } = useQuery({
    queryKey: ["superadmin-llm-usage"],
    queryFn: () => superadminService.llmUsage(30),
    enabled: Boolean(user?.is_superadmin),
  });

  const { data: adminUsers } = useQuery({
    queryKey: ["superadmin-admin-users"],
    queryFn: superadminService.listAdminUsers,
    enabled: Boolean(user?.is_superadmin),
  });

  const approvalMutation = useMutation({
    mutationFn: ({ id, isApproved }: { id: string; isApproved: boolean }) =>
      superadminService.setAdminApproval(id, isApproved),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["superadmin-admin-users"] });
      toast.success(
        row.is_approved ? `${row.email} 사용을 허용했습니다.` : `${row.email} 사용을 차단했습니다.`
      );
    },
    onError: () => toast.error("승인 상태 변경에 실패했습니다."),
  });

  const refreshQueue = () => {
    queryClient.invalidateQueries({ queryKey: ["superadmin-property-queue"] });
    queryClient.invalidateQueries({ queryKey: ["superadmin-services"] });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => superadminService.approveProperty(id),
    onSuccess: (result) => {
      refreshQueue();
      if (result.channex_status === "approved") {
        toast.success("승인 완료 — Channex 연동이 생성되었습니다.");
      } else {
        toast.error("승인은 되었으나 Channex 연결에 실패했습니다. 다시 시도하세요.");
      }
    },
    onError: () => toast.error("승인에 실패했습니다."),
  });

  const transitionMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "integrating" | "connected" }) =>
      superadminService.transitionProperty(id, status),
    onSuccess: () => {
      refreshQueue();
      toast.success("상태가 변경되었습니다.");
    },
    onError: () => toast.error("상태 변경에 실패했습니다."),
  });

  // 임퍼소네이션 — 대상 관리자 토큰을 받아 별도 슬롯에 저장하고 전체 리로드
  // (이전 계정의 쿼리 캐시를 확실히 버리기 위해 SPA 네비게이션 대신 assign).
  const impersonateMutation = useMutation({
    mutationFn: (id: string) => superadminService.impersonateAdmin(id),
    onSuccess: (t) => {
      useAuthStore.getState().startImpersonation({
        token: t.access_token,
        admin: { id: t.admin_id, email: t.email, name: t.name },
      });
      window.location.assign("/admin");
    },
    onError: () => toast.error("임퍼소네이션에 실패했습니다."),
  });

  // 상태별 전이 가능한 다음 단계 — "approve"는 승인 엔드포인트, 나머지는 status 전이.
  const queueOptions = (p: QueuedProperty): { value: string; label: string }[] => {
    switch (p.status) {
      case "pending_approval":
        return [{ value: "approve", label: "승인" }];
      case "pending":
        return [{ value: "approve", label: "승인 재시도" }];
      case "approved":
        return [{ value: "integrating", label: "연동 시작" }];
      case "integrating":
        return [{ value: "connected", label: "연동 완료" }];
      default:
        return [];
    }
  };

  const runQueueAction = (p: QueuedProperty, value: string) => {
    if (value === "approve") approveMutation.mutate(p.id);
    else transitionMutation.mutate({ id: p.id, status: value as "integrating" | "connected" });
  };

  const { data: docs, isFetching } = useQuery({
    queryKey: ["superadmin-collection", serviceId, collection.name, page],
    queryFn: () =>
      superadminService.browseCollection(
        serviceId ?? "",
        collection.name,
        page * PAGE_SIZE,
        PAGE_SIZE
      ),
    enabled: Boolean(user?.is_superadmin && serviceId),
  });

  // Wait for /auth/me before judging; then bounce non-superadmins.
  if (user && !user.is_superadmin) return <Navigate to="/admin" replace />;
  if (!user) return null;

  const lastPage = docs ? Math.max(0, Math.ceil(docs.total / PAGE_SIZE) - 1) : 0;

  // 승인 대기 건수는 상단 배지가 원본이다 — 알림 메일이 못 나가도(SMTP 미설정·발송
  // 실패) 이 숫자는 화면에 남아 24시간 약속의 백스톱이 된다.
  const pendingApprovals = (adminUsers ?? []).filter((a) => !a.is_approved).length;

  const totals = (services ?? []).reduce(
    (acc, s) => {
      acc.properties += s.counts.properties;
      acc.reservations += s.counts.reservations;
      acc.guests += s.counts.guests;
      acc.chat_messages += s.counts.chat_messages;
      return acc;
    },
    { properties: 0, reservations: 0, guests: 0, chat_messages: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow-brass">Operations</p>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 font-display text-2xl font-medium">
            슈퍼어드민
            {pendingApprovals > 0 ? (
              <a
                href="#user-approval"
                title="가입 후 24시간 이내로 처리해드린다고 안내하고 있어요."
                className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium tabular-nums text-amber-800 hover:bg-amber-200"
              >
                승인 대기 {pendingApprovals}명
              </a>
            ) : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            전체 계정의 등록 데이터를 조회하고 숙소 연동을 관리하는 운영용 콘솔입니다.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/super/ota-guide">
            <BookOpen /> OTA 매핑 가이드
          </Link>
        </Button>
      </div>

      <Card className="grid grid-cols-2 divide-border/60 overflow-hidden py-0 sm:grid-cols-3 sm:divide-x lg:grid-cols-5">
        <StatTile icon={Building2} label="계정" value={services?.length ?? 0} />
        <StatTile icon={BedDouble} label="숙소" value={totals.properties} />
        <StatTile icon={CalendarCheck} label="예약" value={totals.reservations} />
        <StatTile icon={Users} label="게스트" value={totals.guests} />
        <StatTile icon={MessageSquareText} label="채팅 메시지" value={totals.chat_messages} />
      </Card>

      {syncHealth && syncHealth.rows.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <RadioTower className="h-4 w-4 text-primary" /> 채널 동기화 상태
              <span className="text-sm font-normal text-muted-foreground">
                실패 {syncHealth.rows.filter((r) => hasSyncFailure(r)).length}건
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              나쁜 것부터 보여드려요. 재고·요금 푸시가 실패로 남아 있으면 OTA가 옛 재고를 팔 수
              있어요.{" "}
              {syncHealth.alerts_enabled
                ? `${syncHealth.alert_after_failures}회 연속 실패하면 운영 알림 메일이 나가요.`
                : "실패 알림 메일은 꺼져 있어요 (메일 발송 설정이 없어요)."}
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>숙소</TableHead>
                  <TableHead>서비스</TableHead>
                  <TableHead>{SYNC_OP_LABEL.availability}</TableHead>
                  <TableHead>{SYNC_OP_LABEL.content}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncHealth.rows.map((row) => (
                  <TableRow key={row.property_id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-muted-foreground">{row.service_name}</TableCell>
                    <TableCell className="text-xs">
                      <SyncStatusCell
                        state={row.sync.availability}
                        staleAfterMinutes={syncHealth.stale_after_minutes}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      <SyncStatusCell
                        state={row.sync.content}
                        staleAfterMinutes={syncHealth.stale_after_minutes}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {llmUsage && llmUsage.rows.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Coins className="h-4 w-4 text-primary" /> LLM 사용량·추정 원가
              <span className="text-sm font-normal text-muted-foreground">
                최근 {llmUsage.days}일 · 합계 {money(totalCost(llmUsage.rows), llmUsage.currency)}
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {llmUsage.since}부터 쌓인 토큰이에요. 비용은 지금 설정된 모델별 단가로 계산한 추정치라
              청구서와 다를 수 있어요.{" "}
              {llmUsage.unpriced_models.length > 0
                ? `단가가 없는 모델(${llmUsage.unpriced_models.join(", ")})은 비용에서 빠졌어요 — config 의 llm.prices 에 채워 주세요.`
                : null}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>숙소</TableHead>
                    <TableHead>서비스</TableHead>
                    <TableHead className="text-right">호출</TableHead>
                    <TableHead className="text-right">입력 토큰</TableHead>
                    <TableHead className="text-right">출력 토큰</TableHead>
                    <TableHead>용도</TableHead>
                    <TableHead>provider</TableHead>
                    <TableHead className="text-right">추정 비용</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {llmUsage.rows.map((row) => (
                    <TableRow key={row.property_id}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="text-muted-foreground">{row.service_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number.format(row.calls)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number.format(row.input_tokens)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {number.format(row.output_tokens)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {breakdown(row.purposes, PURPOSE_LABEL)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {breakdown(row.providers)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {money(row.cost, llmUsage.currency)}
                        {row.unpriced_calls > 0 ? (
                          <div className="text-muted-foreground">
                            단가 미설정 {number.format(row.unpriced_calls)}건 제외
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {adminUsers ? (
        <Card id="user-approval">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCheck className="h-4 w-4 text-primary" /> 사용자 승인
              <span className="text-sm font-normal text-muted-foreground">
                {pendingApprovals}명 대기
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              베타 기간에는 허용된 계정만 로그인할 수 있습니다. 토글을 켜면 즉시 로그인 가능합니다.
              가입자에게는 24시간 이내 처리를 안내하고 있습니다.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>이메일</TableHead>
                  <TableHead>이름</TableHead>
                  <TableHead>가입일</TableHead>
                  <TableHead className="text-right">사용 허용</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminUsers.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.email}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {cellText(a.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={a.is_approved}
                        disabled={approvalMutation.isPending}
                        aria-label={`${a.email} 사용 허용`}
                        onCheckedChange={(checked) =>
                          approvalMutation.mutate({ id: a.id, isApproved: checked })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {adminUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                      데이터 없음
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {queue && queue.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck className="h-4 w-4 text-primary" /> 숙소 연동 큐
              <span className="text-sm font-normal text-muted-foreground">{queue.length}건</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              승인 → Channex 생성, 이후 OTA 매핑을 마치면 연동 완료로 전환하세요. prod는 등록 숙소
              수에 비례해 과금됩니다.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>숙소명</TableHead>
                  <TableHead>서비스</TableHead>
                  <TableHead>신청자</TableHead>
                  <TableHead>연락처</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>OTA</TableHead>
                  <TableHead className="text-right">액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((p) => {
                  const options = queueOptions(p);
                  const badge = QUEUE_BADGE[p.status];
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.service_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.owner_email ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div>{p.support_email || "—"}</div>
                        {p.support_phone ? <div>{p.support_phone}</div> : null}
                      </TableCell>
                      <TableCell>
                        {badge ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        ) : (
                          p.status
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.ota_connections.length > 0
                          ? p.ota_connections.map((c) => (
                              <div key={c.ota} title={c.note || undefined}>
                                {OTA_LABEL[c.ota] ?? c.ota}: {c.hotel_id}
                                {c.account_email ? ` (${c.account_email})` : ""}
                              </div>
                            ))
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {options.length > 0 ? (
                          <Select
                            value=""
                            onValueChange={(v) => runQueueAction(p, v)}
                            disabled={approveMutation.isPending || transitionMutation.isPending}
                          >
                            <SelectTrigger
                              className="ml-auto h-8 w-36 text-xs"
                              aria-label={`${p.name} 상태 변경`}
                            >
                              <SelectValue placeholder="상태 변경" />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {services && services.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">계정별 예약 규모</CardTitle>
            <p className="text-xs text-muted-foreground">
              막대를 누르면 해당 계정의 데이터를 조회합니다.
            </p>
          </CardHeader>
          <CardContent>
            <ServiceBars
              services={services}
              selectedId={serviceId}
              onSelect={(id) => {
                setServiceId(id);
                setPage(0);
              }}
            />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(services ?? []).map((svc) => (
          <button
            key={svc.id}
            type="button"
            onClick={() => {
              setServiceId(svc.id);
              setPage(0);
            }}
            className="text-left"
          >
            <Card
              className={cn(
                "h-full cursor-pointer transition-colors",
                serviceId === svc.id
                  ? "border-primary ring-1 ring-primary"
                  : "hover:bg-secondary/50"
              )}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{svc.name}</CardTitle>
                <p className="truncate text-xs text-muted-foreground">
                  {svc.owner_name} · {svc.owner_email}
                </p>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>숙소 {svc.counts.properties}</span>
                <span>예약 {svc.counts.reservations}</span>
                <span>게스트 {svc.counts.guests}</span>
                <span>채팅 {svc.counts.chat_messages}</span>
                <span>Channex {svc.counts.channex_mappings}</span>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {serviceId ? (
        <div className="space-y-3">
          <Tabs
            value={collection.name}
            onValueChange={(v) => {
              setCollection(COLLECTIONS.find((c) => c.name === v) ?? COLLECTIONS[0]);
              setPage(0);
            }}
          >
            <TabsList className="h-auto flex-wrap">
              {COLLECTIONS.map(({ name, label }) => (
                <TabsTrigger key={name} value={name}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="overflow-x-auto rounded-lg border border-border/60 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  {collection.columns.map(({ key, label }) => (
                    <TableHead key={key} className="whitespace-nowrap">
                      {label}
                    </TableHead>
                  ))}
                  {collection.name === "admin_users" ? (
                    <TableHead className="text-right">액션</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(docs?.items ?? []).map((doc, i) => (
                  <TableRow key={String(doc.id ?? i)}>
                    {collection.columns.map(({ key }) => (
                      <TableCell
                        key={key}
                        className="max-w-96 truncate text-xs"
                        title={cellText(doc[key])}
                      >
                        {cellText(doc[key])}
                      </TableCell>
                    ))}
                    {collection.name === "admin_users" ? (
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={
                            impersonateMutation.isPending || String(doc.email) === user.email
                          }
                          onClick={() => impersonateMutation.mutate(String(doc.id))}
                        >
                          이 계정으로 로그인
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
                {docs && docs.items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        collection.columns.length + (collection.name === "admin_users" ? 1 : 0)
                      }
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      데이터 없음
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              총 {docs?.total ?? 0}건 {isFetching ? "· 불러오는 중…" : ""}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                이전
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                다음
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">계정을 선택하면 등록 데이터가 표시됩니다.</p>
      )}
    </div>
  );
}
